import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Data
import uploadedTableData from "layouts/uploaded/data/uploadedTableData";
import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Button,
  TextField,
  DialogActions,
} from "@mui/material";
import AlertBox from "components/Alert/alert";
import CloseIcon from "@mui/icons-material/Close";
import { getAllVideos } from "services/apiService";
import { approveVideo } from "services/apiService";
import { rejectVideo } from "services/apiService";

function UploadedVideos() {
  const [people, setPeople] = useState([]);
  const [page, setPage] = useState(1);

  const [alert, setAlert] = useState({
    open: false,
    type: "success", // "success" | "error" | "confirm"
    title: "",
    message: "",
  });
  const [open, setOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [pendingAction, setPendingAction] = useState({ id: null, type: null });
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  useEffect(() => {
    getAllVideo();
  }, []);

  const getAllVideo = async () => {
    try {
      const response = await getAllVideos();

      if (response?.success) {
        const flattened = response.data.flatMap(user =>
          user.media
            .filter(media => media.type === "video" && media.isPromotion === false)
            .map(media => ({
              ...media,
              user: user,
            }))
        );
        setPeople(flattened);
      }
    } catch (err) {
      console.error("Fetch users failed:", err);
    }
  };

  const handleView = async (url) => {
    setVideoUrl(url);
    handleOpen(true);
  };

  const handleReject = (id) => {
    setPendingAction({ id, type: "reject" });
    setRejectReason("");
    setRejectDialogOpen(true);
  };

  const handleApprove = (id) => {
    setPendingAction({ id, type: "approve" });
    setAlert({
      open: true,
      type: "confirm",
      title: "Confirm Approval",
      message: "Are you sure you want to approve this video?",
    });
  };

  const handleRejectSubmit = async () => {
    if (!rejectReason.trim()) {
      setAlert({
        open: true,
        type: "error",
        title: "Validation Error",
        message: "Please provide a reason for rejection.",
      });
      return;
    }

    try {
      const obj = {
        videoId: pendingAction?.id,
        rejectionReason: rejectReason.trim(),
      };
      const response = await rejectVideo(obj);
      if (response?.success) {
        setAlert({
          open: true,
          type: "success",
          title: "Rejected!",
          message: response?.message?.message || "The video has been rejected successfully.",
        });
        getAllVideo();
      } else {
        setAlert({
          open: true,
          type: "error",
          title: "Error!",
          message: response?.message || "Failed to reject the video.",
        });
      }
    } catch (error) {
      console.error("Reject API error:", error);
      setAlert({
        open: true,
        type: "error",
        title: "Error!",
        message: "Something went wrong while rejecting the video.",
      });
    } finally {
      setRejectDialogOpen(false);
      setPendingAction({ id: null, type: null });
    }
  };

  const onConfirm = async () => {
    if (!pendingAction.id) return;
    const { id, type } = pendingAction;

    try {
      if (type === "approve") {
        let obj = {
          videoId: id,
        };

        const response = await approveVideo(obj);
        if (response?.success) {
          setAlert({
            open: true,
            type: "success",
            title: "Approved!",
            message: response?.message,
          });
          getAllVideo();
        } else {
          setAlert({ open: true, type: "error", title: "Error", message: response?.message });
        }
      }
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error!",
        message: "Something went wrong while processing your action.",
      });
    } finally {
      setPendingAction({ id: null, type: null });
    }
  };

  const { columns, rows } = useMemo(() => {
    return uploadedTableData({
      peopleList: people,
      onView: handleView,
      onReject: handleReject,
      onApprove: handleApprove,
    });
  }, [people]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <AlertBox
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={() => setAlert({ ...alert, open: false })}
        onConfirm={async () => {
          onConfirm();
        }}
      />

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Uploaded Videos
                </MDTypography>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={{ defaultValue: 10, entries: [10, 15, 20, 35, 50] }}
                  showTotalEntries={true}
                  noEndBorder
                  canSearch={true}
                  page={page}
                  onPageChange={setPage}
                  searchFn={(user, query) => {
                    const lowerQuery = query?.toLowerCase();
                    return (
                      user.uploaded?.props?.name?.toLowerCase().includes(lowerQuery) ||
                      user.continents?.props?.children?.toLowerCase().includes(lowerQuery) ||
                      user?.index?.props?.children?.toLowerCase().includes(lowerQuery) ||
                      user?.status?.props?.children?.props?.badgeContent
                        .toLowerCase()
                        .includes(lowerQuery) ||
                      user?.uploadedAt?.props?.children?.toLowerCase().includes(lowerQuery)
                    );
                  }}
                />
              </MDBox>
            </Card>
            <Dialog
              open={open}
              onClose={handleClose}
              maxWidth="md"
              fullWidth
              PaperProps={{
                sx: { borderRadius: 2 },
              }}
            >
              <DialogTitle>
                Video Preview
                <IconButton
                  aria-label="close"
                  onClick={handleClose}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>

              <DialogContent
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "10px",
                }}
              >
                <video
                  src={videoUrl}
                  controls
                  autoPlay
                  style={{
                    maxHeight: "70vh",
                    width: "auto",
                    borderRadius: "8px",
                  }}
                  disablePictureInPicture={true}
                />
              </DialogContent>
            </Dialog>

            <Dialog
              open={rejectDialogOpen}
              onClose={() => setRejectDialogOpen(false)}
              maxWidth="xs"
              fullWidth
            >
              <DialogTitle>Reject Video</DialogTitle>
              <DialogContent>
                <TextField
                  autoFocus
                  fullWidth
                  multiline
                  margin="dense"
                  label="Reason for rejection"
                  placeholder="Enter the reason for rejecting this video..."
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleRejectSubmit}
                  sx={{
                    color: "beige",
                  }}
                >
                  Save
                </Button>
              </DialogActions>
            </Dialog>
          </Grid>
        </Grid>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default UploadedVideos;
